import { AxiosResponse } from 'axios';
import { createApiClient } from './axios-instance';
import {
  CreditBalanceResponse,
  TransactionResponse,
  CreditTransactionType,
  CreditPackage,
  CREDIT_COSTS,
} from '../types/payment.types';

/**
 * Credit service for handling credit-related API calls
 */
export class CreditService {
  /**
   * Get workspace credit balance
   */
  static async getWorkspaceBalance(
    idToken: string,
    workspaceId: string
  ): Promise<CreditBalanceResponse> {
    const client = createApiClient(idToken);
    const response: AxiosResponse<{ data: CreditBalanceResponse }> = await client.get(
      `/workspaces/${workspaceId}/credits`,
      {
        headers: {
          'X-Workspace-ID': workspaceId,
        },
      }
    );
    return response.data.data;
  }

  /**
   * Deduct credits for a specific feature
   */
  static async deductCredits(
    idToken: string,
    workspaceId: string,
    feature: string,
    amount?: number,
    transaction_metadata?: Record<string, any>
  ): Promise<TransactionResponse> {
    const client = createApiClient(idToken);
    const response: AxiosResponse<{ data: TransactionResponse }> = await client.post(
      `/workspaces/${workspaceId}/credits/deduct`,
      {
        feature,
        amount,
        transaction_metadata,
      },
      {
        headers: {
          'X-Workspace-ID': workspaceId,
        },
      }
    );
    return response.data.data;
  }

  /**
   * Check if workspace has sufficient credits for a feature
   */
  static async canAffordFeature(
    idToken: string,
    workspaceId: string,
    feature: string
  ): Promise<{ canAfford: boolean; required: number; available: number }> {
    const requiredCredits = CREDIT_COSTS[feature] || 0;

    try {
      const balance = await this.getWorkspaceBalance(idToken, workspaceId);
      return {
        canAfford: balance.credits >= requiredCredits,
        required: requiredCredits,
        available: balance.credits,
      };
    } catch (error) {
      return {
        canAfford: false,
        required: requiredCredits,
        available: 0,
      };
    }
  }

  /**
   * Get credit cost for a specific feature
   */
  static getFeatureCost(feature: string): number {
    return CREDIT_COSTS[feature] || 0;
  }

  /**
   * Format credit amount with proper pluralization
   */
  static formatCredits(amount: number | null | undefined): string {
    const safeAmount = amount ?? 0;
    return `${safeAmount.toLocaleString()} credit${safeAmount === 1 ? '' : 's'}`;
  }

  /**
   * Get display color for transaction type
   */
  static getTransactionTypeColor(type: CreditTransactionType): string {
    const colorMap: Record<CreditTransactionType, string> = {
      purchase: 'text-green-600',
      bonus: 'text-green-600',
      refund: 'text-green-600',
      usage: 'text-red-600',
      adjustment: 'text-blue-600',
    };
    return colorMap[type] || 'text-gray-600';
  }

  /**
   * Check if transaction type is a deduction
   */
  static isDeduction(type: CreditTransactionType): boolean {
    const deductionTypes: CreditTransactionType[] = ['usage'];
    return deductionTypes.includes(type);
  }

  /**
   * Format transaction amount with +/- prefix
   */
  static formatTransactionAmount(amount: number, type: CreditTransactionType): string {
    const isDeduction = this.isDeduction(type);
    const prefix = isDeduction ? '-' : '+';
    return `${prefix}${Math.abs(amount).toLocaleString()}`;
  }

  /**
   * Get recommended credit package based on usage patterns
   */
  static getRecommendedPackage(
    recentTransactions: TransactionResponse[],
    availablePackages: CreditPackage[]
  ): number | null {
    // Calculate average monthly usage from recent transactions
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const monthlyUsage = recentTransactions
      .filter((tx) => {
        const txDate = new Date(tx.created_at);
        return (
          txDate >= oneMonthAgo && this.isDeduction(tx.transaction_type as CreditTransactionType)
        );
      })
      .reduce((total, tx) => total + Math.abs(tx.credits_change), 0);

    // Recommend package that covers 2-3 months of usage
    const recommendedCredits = monthlyUsage * 2.5;

    // Find package closest to recommendation
    let bestVariantId: number | null = null;
    let smallestDifference = Infinity;

    availablePackages.forEach((pkg) => {
      if (pkg.variants.length > 0) {
        const difference = Math.abs(pkg.credits_amount - recommendedCredits);
        if (difference < smallestDifference) {
          smallestDifference = difference;
          bestVariantId = pkg.variants[0].id;
        }
      }
    });

    return bestVariantId;
  }

  /**
   * Calculate estimated days remaining based on usage
   */
  static estimateDaysRemaining(
    currentBalance: number,
    recentTransactions: TransactionResponse[]
  ): number | null {
    if (currentBalance <= 0) return 0;

    // Calculate average daily usage from last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentUsage = recentTransactions
      .filter((tx) => {
        const txDate = new Date(tx.created_at);
        return (
          txDate >= thirtyDaysAgo && this.isDeduction(tx.transaction_type as CreditTransactionType)
        );
      })
      .reduce((total, tx) => total + Math.abs(tx.credits_change), 0);

    const dailyAverage = recentUsage / 30;

    if (dailyAverage === 0) return null; // No usage pattern

    return Math.floor(currentBalance / dailyAverage);
  }

  /**
   * Get low credit warning threshold
   */
  static getLowCreditThreshold(): number {
    // Warn when credits can't afford 2 of the most expensive operations
    const maxCost = Math.max(...Object.values(CREDIT_COSTS));
    return maxCost * 2;
  }

  /**
   * Check if credits are running low
   */
  static isLowCredit(balance: number): boolean {
    return balance <= this.getLowCreditThreshold();
  }
}
